import { computed, ref, watch, type Ref } from 'vue'

const STORAGE_KEY_PREFIX = 'job-filter-set-order'

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return window.localStorage
}

const getStorageKey = (userSid: string) => `${STORAGE_KEY_PREFIX}:${userSid}`

const loadPreferredOrder = (userSid: string | null | undefined) => {
  if (!userSid) {
    return []
  }

  const storage = getStorage()
  if (!storage) {
    return []
  }

  try {
    const rawValue = storage.getItem(getStorageKey(userSid))
    if (!rawValue) {
      return []
    }

    const parsedValue = JSON.parse(rawValue)
    return Array.isArray(parsedValue) ? parsedValue.filter((id) => typeof id === 'string') : []
  } catch {
    return []
  }
}

export function usePersistedJobFilterSetOrder(userSid: Ref<string | null | undefined>) {
  const preferredOrder = ref<string[]>([])
  const storageKey = computed(() => (userSid.value ? getStorageKey(userSid.value) : null))
  const reload = () => {
    const loadedPreferredOrder = loadPreferredOrder(userSid.value)
    preferredOrder.value = loadedPreferredOrder

    return loadedPreferredOrder
  }

  const persistPreferredOrder = (nextPreferredOrder: string[]) => {
    const nextStorageKey = storageKey.value
    if (!nextStorageKey) {
      return
    }

    const storage = getStorage()
    if (!storage) {
      return
    }

    if (nextPreferredOrder.length === 0) {
      storage.removeItem(nextStorageKey)
      return
    }

    storage.setItem(nextStorageKey, JSON.stringify(nextPreferredOrder))
  }

  const setPreferredOrder = (nextPreferredOrder: string[]) => {
    preferredOrder.value = nextPreferredOrder
    persistPreferredOrder(nextPreferredOrder)
  }

  const reconcile = (availableIds: string[]) => {
    const persistedIds = preferredOrder.value.filter((id) => availableIds.includes(id))
    const newIds = availableIds.filter((id) => !persistedIds.includes(id))

    setPreferredOrder([...persistedIds, ...newIds])
  }

  const moveToFront = (id: string) => {
    setPreferredOrder([id, ...preferredOrder.value.filter((existingId) => existingId !== id)])
  }

  const reorderVisible = (visibleIds: string[]) => {
    const remainingIds = preferredOrder.value.filter((id) => !visibleIds.includes(id))
    setPreferredOrder([...visibleIds, ...remainingIds])
  }

  watch(
    userSid,
    () => {
      reload()
    },
    { immediate: true },
  )

  return {
    preferredOrder,
    reload,
    reconcile,
    moveToFront,
    reorderVisible,
  }
}
